import { useState } from 'react'
import { Unlink } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { useCanAny } from '@/lib/permissions'
import { useUnlinkAlerts } from '../hooks/useCaseGrouping'

interface Props {
  caseId: string
  alertIds: readonly string[]
  /** Total alerts currently in the case — gates supervisor permission. */
  caseAlertCount: number
}

const SUPERVISOR_THRESHOLD = 5

/**
 * AF-127 — Unlink button.
 *
 * When the case has ≥5 alerts, unlinking requires the `cases:unlink_large`
 * permission. The button is disabled with a tooltip explaining why; we
 * never let the click through to a 403 round-trip.
 *
 * On click, opens a small confirmation dialog with a required reason field
 * (the backend captures it for audit).
 */
export function UnlinkButton({ caseId, alertIds, caseAlertCount }: Props) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const requiresSupervisor = caseAlertCount >= SUPERVISOR_THRESHOLD
  // cases:unlink_large was a phantom permission — never granted by
  // any Go role. The backend gates large-case unlinking on
  // cases:write the same as a small-case unlink; the supervisor
  // ceiling is enforced via a role check on the backend, not a
  // separate granular perm. Use cases:write here so the UI matches.
  const canUnlinkLarge = useCanAny('cases:write')
  const blocked = requiresSupervisor && !canUnlinkLarge

  const unlink = useUnlinkAlerts()

  const submit = () => {
    if (reason.trim().length < 5) return
    unlink.mutate(
      { case_id: caseId, alert_ids: [...alertIds], reason: reason.trim() },
      {
        onSuccess: () => {
          setOpen(false)
          setReason('')
        },
      },
    )
  }

  if (blocked) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {/* span wrapper because disabled buttons don't fire pointer events */}
          <span className="inline-flex">
            <Button size="sm" variant="ghost" disabled>
              <Unlink size={12} />
              Unlink
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          Cases with {SUPERVISOR_THRESHOLD}+ alerts can only be unlinked by a supervisor.
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <>
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        <Unlink size={12} />
        Unlink
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Unlink {alertIds.length} alert{alertIds.length === 1 ? '' : 's'} from case
            </DialogTitle>
            <DialogDescription>
              Recorded in the audit log. The alert{alertIds.length === 1 ? '' : 's'} will
              return to the open queue.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1">
            <Label htmlFor="unlink-reason">Reason</Label>
            <Input
              id="unlink-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this alert being removed from the case?"
              maxLength={200}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={reason.trim().length < 5 || unlink.isPending}
            >
              {unlink.isPending ? 'Unlinking…' : 'Unlink'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
